/**
 * TanStack Router configuration.
 *
 * Routes mirror the shared routing table in docs/01-information-architecture.md §6
 * exactly, so a notification, widget or share link lands on the same screen on web as it
 * does on iOS and Android.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  type RouterHistory,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { TabBar } from '@vibelang/ui';
import type { JSX } from 'react';
import { useApp } from './app-context.js';
import { MonetizationHost } from './screens/monetization-host.js';

const TAB_ROUTES = {
  today: '/',
  learn: '/learn',
  talk: '/talk',
  review: '/review',
  me: '/me',
} as const;

type Tab = keyof typeof TAB_ROUTES;

/** The tab that owns the current path, so the bar marks where the learner actually is. */
function activeTab(pathname: string): Tab {
  for (const tab of ['learn', 'talk', 'review', 'me'] as const) {
    if (pathname === TAB_ROUTES[tab] || pathname.startsWith(`${TAB_ROUTES[tab]}/`)) return tab;
  }
  return 'today';
}

function RootLayout(): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { inActiveSession, inOnboarding, courseId } = useApp();

  return (
    <div className="vl-app">
      <Outlet />
      {/* One host for every monetization surface, so no screen presents a paywall
          itself — the invariant the policy exists to keep. */}
      <MonetizationHost onNavigate={(route) => void navigate({ to: route })} />
      {/*
        Hidden during a session, because sessions are modal and nothing may compete with
        the mic — and before there is a course, because every tab needs one. The bar used
        to render on onboarding step 1, where one tap on Learn replaced the whole app with
        "Something went wrong!": the screen asked for the active course, there was none,
        and `useCourseId` threw. Ten seconds into the product.

        Keyed on `courseId` rather than on `inOnboarding` alone: that flag is set by the
        onboarding screen once it mounts, so it is false on the very first render and on
        any route reached before onboarding runs — which is exactly the case that crashed.
      */}
      {!inActiveSession && !inOnboarding && courseId !== null && (
        <TabBar
          // Was hardcoded to `today`, so the bar marked the wrong tab everywhere else.
          active={activeTab(pathname)}
          onNavigate={(tab) => void navigate({ to: TAB_ROUTES[tab] })}
        />
      )}
    </div>
  );
}

const rootRoute = createRootRoute({ component: RootLayout });

function placeholder(title: string) {
  return function Placeholder(): JSX.Element {
    return (
      <main id="main" className="vl-page">
        <h1>{title}</h1>
      </main>
    );
  };
}

/**
 * The full route table. Screens are attached in `main.tsx`; declaring them all here
 * means a deep link can never 404 because a route was forgotten.
 */
export const ROUTE_PATHS = [
  '/',
  '/onboarding/$step',
  '/learn',
  '/learn/path',
  '/learn/module/$moduleId',
  '/learn/lesson/$lessonId',
  '/learn/lesson/$lessonId/session',
  '/learn/lesson/$lessonId/report/$sessionId',
  '/learn/drill/$drillId',
  '/learn/words',
  '/learn/words/deck',
  '/learn/words/$itemId',
  '/learn/words/games/$gameType',
  '/learn/grammar',
  '/learn/grammar/conjugation',
  '/learn/grammar/$conceptId',
  '/learn/listen',
  '/learn/listen/$itemId',
  '/learn/placement',
  '/learn/challenge',
  '/talk',
  '/talk/scenarios',
  '/talk/scenario/$scenarioId',
  '/talk/scenario/$scenarioId/session',
  '/talk/open',
  '/talk/open/session',
  '/talk/session/$sessionId',
  '/talk/texting/$scenarioId',
  '/talk/scenario/$scenarioId/report/$sessionId',
  '/shared/$shareToken',
  '/talk/debate',
  '/talk/interview',
  '/talk/roleplay/new',
  '/talk/tutors',
  '/review',
  '/review/session',
  '/review/mistakes',
  '/review/clinic',
  '/review/clinic/$phoneme',
  '/me',
  '/me/level',
  '/me/reports',
  '/me/reports/$sessionId',
  '/me/reports/$sessionId/transcript',
  '/me/achievements',
  '/me/saved',
  '/me/leagues',
  '/me/certificate',
  '/me/assessment',
  '/me/plans',
  '/me/settings',
  '/library',
  '/workbook',
] as const;

export type RoutePath = (typeof ROUTE_PATHS)[number];

const TITLES: Record<RoutePath, string> = {
  '/': 'Today',
  '/onboarding/$step': 'Getting started',
  '/learn': 'Learn',
  '/learn/path': 'Course path',
  '/learn/module/$moduleId': 'Module',
  '/learn/lesson/$lessonId': 'Lesson',
  '/learn/lesson/$lessonId/session': 'Lesson',
  '/learn/lesson/$lessonId/report/$sessionId': 'Report card',
  '/learn/drill/$drillId': 'Drill',
  '/learn/words': 'Words',
  '/learn/words/deck': 'Word deck',
  '/learn/words/$itemId': 'Word',
  '/learn/words/games/$gameType': 'Word game',
  '/learn/grammar': 'Grammar',
  '/learn/grammar/conjugation': 'Conjugation trainer',
  '/learn/grammar/$conceptId': 'Grammar concept',
  '/learn/listen': 'Listen',
  '/learn/listen/$itemId': 'Listening',
  '/learn/placement': 'Test my level',
  '/learn/challenge': 'Daily challenge',
  '/talk': 'Talk',
  '/talk/scenarios': 'Scenarios',
  '/talk/scenario/$scenarioId': 'Scenario',
  '/talk/scenario/$scenarioId/session': 'Scenario',
  '/talk/open': 'Open Talk',
  '/talk/open/session': 'Open Talk',
  '/talk/session/$sessionId': 'Conversation',
  '/talk/texting/$scenarioId': 'Texting scenario',
  '/talk/scenario/$scenarioId/report/$sessionId': 'Conversation report',
  '/shared/$shareToken': 'Shared role-play',
  '/talk/debate': 'Debate',
  '/talk/interview': 'Interview',
  '/talk/roleplay/new': 'Role-play Lab',
  '/talk/tutors': 'Tutors',
  '/review': 'Review',
  '/review/session': 'Review',
  '/review/mistakes': 'Mistakes',
  '/review/clinic': 'Pronunciation clinic',
  '/review/clinic/$phoneme': 'Sound drill',
  '/me': 'Progress',
  '/me/level': 'Your level',
  '/me/reports': 'Report cards',
  '/me/reports/$sessionId': 'Report card',
  '/me/reports/$sessionId/transcript': 'Transcript',
  '/me/achievements': 'Achievements',
  '/me/saved': 'Saved',
  '/me/leagues': 'Leagues',
  '/me/certificate': 'Certificate',
  '/me/assessment': 'Monthly assessment',
  '/me/plans': 'Compare plans',
  '/me/settings': 'Settings',
  '/library': 'Library',
  '/workbook': 'Workbook',
};

export interface RouteComponents {
  readonly [path: string]: () => JSX.Element;
}

/** Builds the router, letting the host override individual screens. */
/**
 * `history` lets a test drive the real router at a chosen path rather than mounting a
 * screen component on its own. Parameterised screens read their ids from the router, so
 * mounting them bare tests something the product never does.
 */
export function buildRouter(components: RouteComponents = {}, history?: RouterHistory) {
  const children = ROUTE_PATHS.map((path) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path,
      component: components[path] ?? placeholder(TITLES[path]),
    }),
  );

  const routeTree = rootRoute.addChildren(children);

  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    // Scroll restoration matters on the course path, which can be long.
    scrollRestoration: true,
    ...(history === undefined ? {} : { history }),
  });
}

export type AppRouter = ReturnType<typeof buildRouter>;
